import { useNewMobileExperience } from "../../contexts/StaticSiteDataContext";
import SitePreviewContext from "../../website/components/SitePreviewContext";
import { SwipePanelCloseButton } from "../../website/components/online-invitation/mobile/SwipePanelCloseButton";
import { css } from "@emotion/react";
import { Icon, IconButton, theme } from "@minted/minted-components";
import { TextLink } from "@minted/minted-components";
import React, { memo, useContext, useMemo } from "react";

const styles = {
  row: css`
    align-items: center;
    display: flex;
    justify-content: space-between;
  `,
  sectionTitle: (isMobile: boolean) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing.x2};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    ${isMobile
      ? theme.typeStyles.titleSmallStyles
      : theme.typeStyles.titleMediumStyles}
  `,
};

export type SectionHeaderProps = {
  editLabel?: string;
  editLinkType?: "primary" | "secondary";
  onBack?: () => void;
  onCancel?: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
  title: string;
};

/**
 * Header component for section forms and collapsed sections.
 * Provides consistent header with title and optional action buttons based on provided callbacks.
 *
 * @example
 * ```tsx
 * // Edit mode (new mobile experience) - shows close button
 * <SectionHeader title="Button" onCancel={handleCancel} />
 *
 * // Collapsed mode - shows edit button
 * <SectionHeader title="Button" onEdit={handleEdit} />
 *
 * // Simple mode - title only
 * <SectionHeader title="Button" />
 *
 * // With back button
 * <SectionHeader title="Button" onBack={handleBack} />
 * ```
 */
const SectionHeader = ({
  editLabel = "Edit",
  editLinkType = "secondary",
  onBack,
  onCancel,
  onEdit,
  onRemove,
  title,
}: SectionHeaderProps) => {
  const useNewMobileExperienceFlag = useNewMobileExperience();
  const { isMobile } = useContext(SitePreviewContext);

  const actionButton = useMemo(() => {
    // Show close button only when editing in new mobile experience
    if (useNewMobileExperienceFlag && onCancel) {
      return <SwipePanelCloseButton />;
    }

    if (onEdit) {
      return (
        <div data-cy="sections_collapsedEditButton">
          <TextLink
            onClick={onEdit}
            size="small"
            text={editLabel}
            type={editLinkType}
          />
        </div>
      );
    }

    if (onRemove) {
      return (
        <div data-cy="sections_collapsedRemoveButton">
          <TextLink
            onClick={onRemove}
            size="small"
            text="Remove"
            type="secondary"
          />
        </div>
      );
    }

    return null;
  }, [
    editLabel,
    editLinkType,
    useNewMobileExperienceFlag,
    onCancel,
    onEdit,
    onRemove,
  ]);

  const backButton = useMemo(() => {
    if (onBack) {
      return (
        <div>
          <IconButton
            iconType={Icon.types["arrow-left"]}
            onClick={onBack}
            size={IconButton.sizes.large}
            type={IconButton.types.secondary}
          />
        </div>
      );
    }
    return null;
  }, [onBack]);

  return (
    <div css={styles.row}>
      <div
        css={styles.sectionTitle(isMobile)}
        data-cy="sections_collapsedPrimaryText"
      >
        {backButton}
        {title}
      </div>
      {actionButton}
    </div>
  );
};

const MemoizedSectionHeader = memo(SectionHeader);

export { MemoizedSectionHeader as SectionHeader };
